'use client';

import { createContext, useContext, useMemo, useEffect, useSyncExternalStore, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '@/lib/api';
import { SubscriptionDTO } from '@/types';
import { subscribeToServerLocked, getServerLockedOrg, setServerLockedOrg } from '@/lib/subscriptionSignal';
import { useGlobalOrganization } from '@/hooks/useGlobalOrganization';

interface SubscriptionContextType {
    subscription: SubscriptionDTO | undefined;
    isLoading: boolean;
    /** No writes allowed: the trial or paid period has lapsed, but data is still readable. */
    isReadOnly: boolean;
    /** Everything is denied — the app should show the subscribe screen. */
    isLocked: boolean;
    /** Days left in the trial, or null when not on a trial. */
    trialDaysLeft: number | null;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

/**
 * Loads the current organization's subscription.
 *
 * Transitions (trial expiring, a plan change) are picked up by refetching rather than being
 * pushed: the data is considered fresh for a minute and TanStack refetches on window focus.
 * Worst case the UI shows an active state for up to a minute after expiry, while the server
 * is already rejecting the writes — so this is a presentation delay, not a permission hole.
 */
export function SubscriptionProvider({ children }: { children: ReactNode }) {
    const { selectedOrgId } = useGlobalOrganization();

    const { data: subscription, isLoading } = useQuery<SubscriptionDTO>({
        // Keyed by organization: switching org changes x-org-id but would otherwise reuse the
        // previous organization's cached subscription, so the banner and lock screen could
        // describe the wrong organization until the entry went stale.
        queryKey: ['subscription', selectedOrgId],
        queryFn: () => apiService.getSubscription(),
        enabled: !!selectedOrgId,
        staleTime: 60_000,
        // A focused tab never refetches on its own — focus events only fire when the window
        // regains focus — so without an interval a session left open would never notice its
        // trial expiring. Poll a little more often than the staleTime.
        refetchInterval: 5 * 60_000,
        refetchIntervalInBackground: false,
        retry: 3,
    });

    // Set by the API error interceptor when the server denies with subscription_locked, and
    // only honoured when it refers to the organization currently selected.
    const serverLockedOrgId = useSyncExternalStore(subscribeToServerLocked, getServerLockedOrg);
    const serverLocked = !!selectedOrgId && serverLockedOrgId === selectedOrgId;

    // A successful fetch is the authority: it clears a stale flag (e.g. after the subscription
    // is renewed) and sets it when the subscription really is locked.
    useEffect(() => {
        if (!subscription || !selectedOrgId) return;
        setServerLockedOrg(subscription.state === 'Locked' ? selectedOrgId : null);
    }, [subscription, selectedOrgId]);

    const value = useMemo(() => ({
        subscription,
        isLoading,
        isReadOnly: subscription?.state === 'ReadOnly',
        isLocked: subscription?.state === 'Locked' || serverLocked,
        trialDaysLeft: subscription?.trial_days_left ?? null,
    }), [subscription, isLoading, serverLocked]);

    return (
        <SubscriptionContext.Provider value={value}>
            {children}
        </SubscriptionContext.Provider>
    );
}

export function useSubscription() {
    const context = useContext(SubscriptionContext);
    if (context === undefined) {
        throw new Error('useSubscription must be used within a SubscriptionProvider');
    }
    return context;
}
